'use client';

import { useId, type Dispatch } from 'react';
import type { HingeSide } from '../drawing/door';
import {
  CUP_D,
  EDGE_STD,
  HANDLE_CENTRES,
  HOLE_D,
  MAX_HINGES,
  PIECE_LABEL,
  handleIssues,
  hingeIssues,
  hingedLength,
  isFlap,
  standardCount,
  type HandleDrillType,
  type HandleProduct,
  type Piece,
} from './drilling';
import { NumberField } from './NumberField';
import { hingeSummary, type DoorOrderAction, type HandleMove } from './orderState';

const SIDE_LABEL: Record<HingeSide, string> = {
  left: 'Left',
  right: 'Right',
  top: 'Top (up-and-over)',
  bottom: 'Bottom (drop-down)',
  none: 'No drilling',
};

function Checks({ issues, ok }: { issues: string[]; ok: string }) {
  return issues.length ? (
    <ul className="sk-db__warn" role="status">
      {issues.map((issue) => (
        <li key={issue}>{`⚠ ${issue}`}</li>
      ))}
    </ul>
  ) : (
    <p className="sk-db__ok" role="status">{`✓ ${ok}`}</p>
  );
}

/**
 * The drilling editor for the selected piece: hinge side, count, cup edge
 * distance and each cup's position; then handle drilling (none, one hole or
 * two), hole centres, orientation and the exact centre. Every change is checked
 * as it is made, with the problem in plain words.
 */
export function DrillEditor({
  piece,
  index,
  total,
  product,
  dispatch,
}: {
  piece: Piece;
  index: number;
  total: number;
  product: HandleProduct;
  dispatch: Dispatch<DoorOrderAction>;
}) {
  const id = useId();
  const hinge = piece.hinge;
  const length = hinge.side === 'none' ? 0 : hingedLength(piece, hinge.side);
  const measuredFrom = isFlap(hinge.side) ? 'from the left edge' : 'from the top edge';
  const handle = piece.handle;
  const handleLabel =
    handle.type === 'none' ? 'no handle holes' : handle.type === 'single' ? '1 hole' : `2 holes, ${handle.centres}mm centres, ${handle.orient}`;
  const types: Array<[HandleDrillType, string]> = [
    ['none', 'None'],
    ['single', 'Single hole (knob)'],
    ['double', 'Two holes (bar or cup)'],
  ];
  const moves: Array<[HandleMove, string]> = [
    ['centre-x', 'Centre across'],
    ['top', 'Near top'],
    ['middle', 'Middle'],
    ['bottom', 'Near bottom'],
    ['standard', handle.custom ? 'Reset to standard' : 'Standard position ✓'],
  ];

  return (
    <section className="sk-db__drill" aria-labelledby={`${id}-title`}>
      <div className="sk-db__drillhead">
        <span className="sk-db__num sk-db__num--on" aria-hidden="true">
          {index + 1}
        </span>
        <strong id={`${id}-title`}>{`${PIECE_LABEL[piece.kind]} · ${piece.h} × ${piece.w} mm · ×${piece.qty}`}</strong>
        <span className="sk-db__aside" style={{ marginLeft: 0 }}>
          {hingeSummary(piece)}
        </span>
        <span className="sk-db__navbtns">
          <button type="button" className="sk-db__mini" disabled={index === 0} onClick={() => dispatch({ type: 'step', by: -1 })}>
            ‹ Previous
          </button>
          <button type="button" className="sk-db__mini" disabled={index >= total - 1} onClick={() => dispatch({ type: 'step', by: 1 })}>
            Next ›
          </button>
        </span>
      </div>

      <div className="sk-db__field">
        <span className="sk-db__label" id={`${id}-side`}>
          Hinge side
        </span>
        <div className="sk-seg" role="group" aria-labelledby={`${id}-side`}>
          {(Object.keys(SIDE_LABEL) as HingeSide[]).map((side) => (
            <button key={side} type="button" aria-pressed={side === hinge.side} onClick={() => dispatch({ type: 'hingeSide', index, side })}>
              {SIDE_LABEL[side]}
            </button>
          ))}
        </div>
      </div>

      {hinge.side === 'none' ? (
        <p className="sk-db__hint" style={{ margin: 0 }}>
          No hinge holes will be drilled. Choose a side to add them.
        </p>
      ) : (
        <>
          <div className="sk-db__drillgrid">
            <div className="sk-db__field">
              <label className="sk-db__label" htmlFor={`${id}-count`}>
                Number of hinges
              </label>
              <select
                id={`${id}-count`}
                className="sk-db__input"
                value={hinge.count}
                onChange={(e) => dispatch({ type: 'hingeCount', index, count: Number(e.target.value) })}
              >
                {Array.from({ length: MAX_HINGES }, (_, k) => k + 1).map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
              <span className="sk-db__hint" style={{ margin: 0 }}>{`standard for ${length}mm: ${standardCount(length)}`}</span>
            </div>
            <div className="sk-db__field">
              <label className="sk-db__label" htmlFor={`${id}-edge`}>
                Cup centre from edge
              </label>
              <NumberField id={`${id}-edge`} value={hinge.edge} step={0.5} suffix="mm" onCommit={(edge) => dispatch({ type: 'hingeEdge', index, edge })} />
              <span className="sk-db__hint" style={{ margin: 0 }}>{`standard ${EDGE_STD} · Ø${CUP_D}mm cup`}</span>
            </div>
            <div className="sk-db__field">
              <span className="sk-db__label">Positions</span>
              <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={() => dispatch({ type: 'hingeReset', index })}>
                {hinge.custom ? 'Reset to standard' : 'Standard positions ✓'}
              </button>
              <span className="sk-db__hint" style={{ margin: 0 }}>
                {hinge.custom ? 'your own positions' : 'set automatically'}
              </span>
            </div>
          </div>
          <div>
            <span className="sk-db__label">{`Hinge centres, measured ${measuredFrom}`}</span>
            <ul className="sk-db__positions">
              {hinge.positions.map((position, at) => (
                <li key={at} className="sk-db__field">
                  <label className="sk-db__label" htmlFor={`${id}-pos-${at}`}>{`Hinge ${at + 1}`}</label>
                  <NumberField
                    id={`${id}-pos-${at}`}
                    value={position}
                    suffix="mm"
                    onCommit={(value) => dispatch({ type: 'hingePosition', index, at, value })}
                  />
                </li>
              ))}
            </ul>
          </div>
          <Checks issues={hingeIssues(piece)} ok="Hinge drilling fits this piece" />
        </>
      )}

      <div className="sk-db__section">
        <div className="sk-db__drillhead">
          <strong>Handle drilling</strong>
          <span className="sk-db__aside" style={{ marginLeft: 0 }}>
            {`${handleLabel}${product === 'none' && handle.type !== 'none' ? ' · drill only, for your own handles' : ''}`}
          </span>
        </div>
        <div className="sk-seg" role="group" aria-label="Handle drilling">
          {types.map(([drill, label]) => (
            <button key={drill} type="button" aria-pressed={drill === handle.type} onClick={() => dispatch({ type: 'handleType', index, drill })}>
              {label}
            </button>
          ))}
        </div>
        {handle.type !== 'none' && (
          <>
            <div className="sk-db__drillgrid">
              {handle.type === 'double' && (
                <>
                  <div className="sk-db__field">
                    <label className="sk-db__label" htmlFor={`${id}-centres`}>
                      Hole centres
                    </label>
                    <select
                      id={`${id}-centres`}
                      className="sk-db__input"
                      value={handle.centres}
                      onChange={(e) => dispatch({ type: 'handleCentres', index, centres: Number(e.target.value) })}
                    >
                      {HANDLE_CENTRES.map((c) => (
                        <option key={c} value={c}>{`${c} mm`}</option>
                      ))}
                    </select>
                    <span className="sk-db__hint" style={{ margin: 0 }}>
                      your handle&rsquo;s screw spacing
                    </span>
                  </div>
                  <div className="sk-db__field">
                    <span className="sk-db__label" id={`${id}-orient`}>
                      Orientation
                    </span>
                    <div className="sk-seg" role="group" aria-labelledby={`${id}-orient`}>
                      {(['vertical', 'horizontal'] as const).map((orient) => (
                        <button key={orient} type="button" aria-pressed={orient === handle.orient} onClick={() => dispatch({ type: 'handleOrient', index, orient })}>
                          {orient === 'vertical' ? 'Vertical' : 'Horizontal'}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div className="sk-db__field">
                <label className="sk-db__label" htmlFor={`${id}-hx`}>
                  Centre from left edge
                </label>
                <NumberField id={`${id}-hx`} value={handle.x} suffix="mm" onCommit={(value) => dispatch({ type: 'handleAt', index, field: 'x', value })} />
                <span className="sk-db__hint" style={{ margin: 0 }}>
                  seen from the front
                </span>
              </div>
              <div className="sk-db__field">
                <label className="sk-db__label" htmlFor={`${id}-hy`}>
                  Centre from top edge
                </label>
                <NumberField id={`${id}-hy`} value={handle.y} suffix="mm" onCommit={(value) => dispatch({ type: 'handleAt', index, field: 'y', value })} />
                <span className="sk-db__hint" style={{ margin: 0 }}>{`Ø${HOLE_D}mm through-holes`}</span>
              </div>
            </div>
            <div className="sk-db__moves" role="group" aria-label="Move the handle">
              {moves.map(([move, label]) => (
                <button key={move} type="button" className="sk-db__mini" onClick={() => dispatch({ type: 'handleMove', index, move })}>
                  {label}
                </button>
              ))}
            </div>
            <Checks issues={handleIssues(piece)} ok="Handle holes fit this piece" />
          </>
        )}
      </div>
      <p className="sk-db__hint" style={{ margin: 0 }}>
        Tip: switch the viewer to <b>Back &amp; drilling</b> to check every hole against your old door.
      </p>
    </section>
  );
}
